<template>
  <div class="servers-fleet-board">
    <div class="servers-fleet-board__header">
      <div>
        <h1>Fleet inventory</h1>
        <p>Every server Goshawk knows about, whether or not it's actually reporting.</p>
      </div>
      <div class="servers-fleet-board__header-actions">
        <button type="button" class="servers-fleet-board__btn" @click="adoptionKeysPanelOpen = true">
          Adoption keys
        </button>
        <button type="button" class="servers-fleet-board__btn servers-fleet-board__btn--primary" @click="openManualEnrolment">
          Enrol a server
        </button>
      </div>
    </div>

    <div v-if="loadError" class="servers-fleet-board__error">{{ loadError }}</div>

    <div class="servers-fleet-board__summary">
      <div class="servers-fleet-board__sum-card servers-fleet-board__sum-card--total">
        <div class="servers-fleet-board__sval">{{ summary.total ?? '—' }}</div>
        <div class="servers-fleet-board__slabel">Total servers</div>
      </div>
      <div class="servers-fleet-board__sum-card servers-fleet-board__sum-card--healthy">
        <div class="servers-fleet-board__sval">{{ summary.healthy ?? '—' }}</div>
        <div class="servers-fleet-board__slabel">Healthy</div>
      </div>
      <div class="servers-fleet-board__sum-card servers-fleet-board__sum-card--warning">
        <div class="servers-fleet-board__sval">{{ summary.warning ?? '—' }}</div>
        <div class="servers-fleet-board__slabel">Warning</div>
      </div>
      <div class="servers-fleet-board__sum-card servers-fleet-board__sum-card--critical">
        <div class="servers-fleet-board__sval">{{ summary.critical ?? '—' }}</div>
        <div class="servers-fleet-board__slabel">Critical</div>
      </div>
      <div class="servers-fleet-board__sum-card servers-fleet-board__sum-card--unmanaged">
        <div class="servers-fleet-board__sval">{{ summary.unmanaged ?? '—' }}</div>
        <div class="servers-fleet-board__slabel">Unmanaged</div>
      </div>
    </div>

    <div class="servers-fleet-board__layout">
      <div class="servers-fleet-board__rail">
        <div class="servers-fleet-board__rail-head">
          <h4>Filters</h4>
          <span v-if="hasActiveFilters" class="servers-fleet-board__clear" @click="clearFilters">Clear</span>
        </div>

        <div v-for="group in facetGroups" :key="group.key" class="servers-fleet-board__facet-group">
          <div class="servers-fleet-board__facet-title">{{ group.title }}</div>
          <div
            v-for="term in group.terms"
            :key="term.key"
            class="servers-fleet-board__facet-row"
            :class="{ 'servers-fleet-board__facet-row--active': isActive(group.key, term.key) }"
            @click="toggleFacet(group.key, term.key)"
          >
            <span class="servers-fleet-board__facet-box"></span>
            <span class="servers-fleet-board__facet-label">{{ term.label }}</span>
            <span class="servers-fleet-board__facet-count">{{ term.count }}</span>
          </div>
        </div>
      </div>

      <div class="servers-fleet-board__grid">
        <table>
          <thead>
            <tr>
              <th>Server</th>
              <th>Environment</th>
              <th>Role</th>
              <th>Uptime (30d)</th>
              <th>Load</th>
              <th>Disk</th>
              <th>Cert</th>
              <th>Agent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9" class="servers-fleet-board__loading">Loading fleet…</td>
            </tr>
            <tr v-else-if="!filteredServers.length">
              <td colspan="9" class="servers-fleet-board__loading">No servers match the current filters.</td>
            </tr>
            <tr v-for="server in filteredServers" :key="server.id" @click="viewServer(server)">
              <td>
                <div class="servers-fleet-board__srv-cell">
                  <span class="servers-fleet-board__health-dot" :class="healthDotClass(server)"></span>
                  <div>
                    <span class="servers-fleet-board__srv-name">{{ server.display_name || server.hostname }}</span>
                    <span class="servers-fleet-board__srv-provider">{{ providerLabel(server) }}</span>
                  </div>
                </div>
              </td>
              <td><span v-if="server.tags.environment" class="servers-fleet-board__pill servers-fleet-board__pill--env">{{ taxonomyLabel('environment', server.tags.environment) }}</span></td>
              <td>
                <span
                  v-for="role in server.tags.server_role"
                  :key="role"
                  class="servers-fleet-board__pill servers-fleet-board__pill--role"
                >{{ taxonomyLabel('server_role', role) }}</span>
              </td>
              <td class="servers-fleet-board__num">
                <span v-if="uptimeByServer[server.id] !== undefined && uptimeByServer[server.id] !== null">{{ uptimeByServer[server.id].toFixed(3) }}%</span>
                <span v-else class="servers-fleet-board__na">—</span>
              </td>
              <td>
                <div v-if="server.vitals_load !== null" class="servers-fleet-board__meter-row">
                  <span class="servers-fleet-board__meter"><span class="servers-fleet-board__meter-fill" :class="loadBarTone(server.vitals_load)" :style="{ width: loadBarWidthPct(server.vitals_load) + '%' }"></span></span>
                  <span class="servers-fleet-board__meter-val">{{ server.vitals_load.toFixed(2) }}</span>
                </div>
                <span v-else class="servers-fleet-board__na">—</span>
              </td>
              <td>
                <div v-if="server.vitals_disk_pct !== null" class="servers-fleet-board__meter-row">
                  <span class="servers-fleet-board__meter"><span class="servers-fleet-board__meter-fill" :class="meterToneForPct(server.vitals_disk_pct)" :style="{ width: server.vitals_disk_pct + '%' }"></span></span>
                  <span class="servers-fleet-board__meter-val">{{ Math.round(server.vitals_disk_pct) }}%</span>
                </div>
                <span v-else class="servers-fleet-board__na">—</span>
              </td>
              <td>
                <span class="servers-fleet-board__cert-pill" :class="'servers-fleet-board__cert-pill--' + certExpiryTone(server)">{{ certExpiryLabel(server) }}</span>
              </td>
              <td @click.stop>
                <AgentStatusBadge :status="server.agent_status" :sub="agentStatusSub(server)" />
              </td>
              <td @click.stop>
                <button
                  v-if="server.agent_status === 'unmanaged'"
                  type="button"
                  class="servers-fleet-board__install-btn"
                  @click="openAdoptEnrolment(server)"
                >Install agent</button>
                <span v-else class="servers-fleet-board__row-view">View &rarr;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ServerEnrolmentModal
      v-if="enrolmentModal.open"
      :api-base="apiBase"
      :hub="hub"
      :adopt-target="enrolmentModal.target"
      @close="closeEnrolmentModal"
      @enrolled="refresh"
    />

    <AdoptionKeysPanel
      v-if="adoptionKeysPanelOpen"
      :api-base="apiBase"
      @close="adoptionKeysPanelOpen = false"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import AgentStatusBadge from '@root/components/Servers/AgentStatusBadge.vue';
import ServerEnrolmentModal from '@root/components/Servers/ServerEnrolmentModal.vue';
import AdoptionKeysPanel from '@root/components/Servers/AdoptionKeysPanel.vue';
import {
  agentStatusSub,
  certExpiryLabel,
  certExpiryTone,
  healthDotClass,
  loadBarTone,
  loadBarWidthPct,
  meterToneForPct,
  taxonomyLabel,
} from '@root/services/serverFormat';

// `fleet-inventory.html` — mounted at `/servers` via
// `app\module\servers\Controller\ServersDisplayController::index()`. Owns
// its own data-fetching/filtering/state, mirroring Town Centre's
// `Hub/HubServerBoard.vue` shape (a whole owned page) rather than being
// assembled from many small server-driven building blocks.
const props = defineProps({
  ...commonProps,
  apiBase: {
    type: String,
    default: '/api/v1/servers',
  },
});

const api = useApi();
const router = useRouter();

const servers = ref([]);
const summary = reactive({ total: null, healthy: null, warning: null, critical: null, unmanaged: null });
const uptimeByServer = reactive({});
const hub = ref(null);
const loading = ref(false);
const loadError = ref(null);

const filters = reactive({ environment: new Set(), server_role: new Set(), provider: new Set() });

const enrolmentModal = reactive({ open: false, target: null });
const adoptionKeysPanelOpen = ref(false);

let pollHandle = null;

async function loadHub() {
  try {
    const data = await api.get(`${props.apiBase}/hub`);
    hub.value = data.hub;
  } catch {
    hub.value = null;
  }
}

async function loadServers() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await api.get(props.apiBase);
    servers.value = data.servers || [];
    Object.assign(summary, data.summary || {});
    loadUptimes();
  } catch (err) {
    loadError.value = 'Could not load the fleet — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

// Uptime is only exposed per-server (`/{id}/uptime`), not in the bulk list
// payload — best-effort N+1 fetch for the fleet's small server count (no
// pagination on the list endpoint either, so this is sized to match).
// Skipped for unmanaged/enrolling rows, which render "—" unconditionally
// (an agent that's never reported has no uptime interval history).
async function loadUptimes() {
  const eligible = servers.value.filter((s) => s.agent_status !== 'unmanaged' && s.agent_status !== 'enrolling');
  await Promise.allSettled(eligible.map(async (server) => {
    try {
      const data = await api.get(`${props.apiBase}/${server.id}/uptime`);
      uptimeByServer[server.id] = data?.windows?.['30d']?.uptime_pct ?? null;
    } catch {
      uptimeByServer[server.id] = null;
    }
  }));
}

async function refresh() {
  await loadServers();
}

function providerLabel(server) {
  return taxonomyLabel('provider', server.tags.provider) || '—';
}

// ── Facets (computed client-side from the already-fetched full list — the
// list endpoint has no pagination, so this avoids a round-trip per
// checkbox toggle) ──────────────────────────────────────────────────────
const facetGroups = computed(() => {
  const groups = [
    { key: 'environment', title: 'Environment', counts: new Map() },
    { key: 'server_role', title: 'Server role', counts: new Map() },
    { key: 'provider', title: 'Provider', counts: new Map() },
  ];

  for (const server of servers.value) {
    const env = server.tags.environment;
    if (env) {
      groups[0].counts.set(env, (groups[0].counts.get(env) || 0) + 1);
    }
    for (const role of server.tags.server_role || []) {
      groups[1].counts.set(role, (groups[1].counts.get(role) || 0) + 1);
    }
    const provider = server.tags.provider;
    if (provider) {
      groups[2].counts.set(provider, (groups[2].counts.get(provider) || 0) + 1);
    }
  }

  return groups.map((group) => ({
    key: group.key,
    title: group.title,
    terms: [...group.counts.entries()]
      .map(([key, count]) => ({ key, count, label: taxonomyLabel(group.key, key) }))
      .sort((a, b) => b.count - a.count),
  }));
});

function isActive(facetKey, termKey) {
  return filters[facetKey].has(termKey);
}

function toggleFacet(facetKey, termKey) {
  const set = filters[facetKey];
  if (set.has(termKey)) {
    set.delete(termKey);
  } else {
    set.add(termKey);
  }
}

const hasActiveFilters = computed(() => (
  filters.environment.size > 0 || filters.server_role.size > 0 || filters.provider.size > 0
));

function clearFilters() {
  filters.environment.clear();
  filters.server_role.clear();
  filters.provider.clear();
}

const filteredServers = computed(() => servers.value.filter((server) => {
  if (filters.environment.size && !filters.environment.has(server.tags.environment)) {
    return false;
  }
  if (filters.provider.size && !filters.provider.has(server.tags.provider)) {
    return false;
  }
  if (filters.server_role.size) {
    const roles = server.tags.server_role || [];
    if (![...filters.server_role].some((role) => roles.includes(role))) {
      return false;
    }
  }
  return true;
}));

function viewServer(server) {
  router.push(`/servers/${server.id}`);
}

function openManualEnrolment() {
  enrolmentModal.target = null;
  enrolmentModal.open = true;
}

function openAdoptEnrolment(server) {
  enrolmentModal.target = server;
  enrolmentModal.open = true;
}

function closeEnrolmentModal() {
  enrolmentModal.open = false;
  enrolmentModal.target = null;
}

onMounted(() => {
  loadHub();
  loadServers();
  pollHandle = setInterval(loadServers, 20000);
});

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});
</script>

<style lang="scss">
.servers-fleet-board {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 18px;
    flex-wrap: wrap;

    h1 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    p {
      font-size: 13px;
      color: var(--theme-text-secondary);
      margin: 0;
      max-width: 70ch;
    }
  }

  &__header-actions {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    white-space: nowrap;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }

  &__summary {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__sum-card {
    position: relative;
    overflow: hidden;
    padding: 13px 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--healthy::before { background: var(--theme-status-success); }
    &--healthy .servers-fleet-board__sval { color: #1c7a4d; }
    &--warning::before { background: var(--theme-status-warning); }
    &--warning .servers-fleet-board__sval { color: #8a5a0d; }
    &--critical::before { background: var(--theme-status-danger); }
    &--critical .servers-fleet-board__sval { color: #a53232; }
    &--unmanaged::before { background: var(--theme-text-tertiary); }
  }

  &__sval {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  &__slabel {
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
  }

  &__layout {
    display: grid;
    grid-template-columns: 216px 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__rail {
    padding: 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
  }

  &__rail-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
    }
  }

  &__clear {
    font-size: 11px;
    color: var(--theme-accent-dark);
    font-weight: 600;
    cursor: pointer;
  }

  &__facet-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__facet-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--theme-text-tertiary);
    margin-bottom: 6px;
  }

  &__facet-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4.5px 2px;
    font-size: 12.5px;
    color: var(--theme-text-secondary);
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      color: var(--theme-text-primary);
      background: var(--theme-bg-content);
    }

    &--active {
      color: var(--theme-text-primary);
      font-weight: 600;
    }

    &--active &-box {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
    }
  }

  &__facet-box {
    width: 13px;
    height: 13px;
    border-radius: 3px;
    border: 1.5px solid var(--theme-border-strong);
    flex-shrink: 0;

    .servers-fleet-board__facet-row--active & {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
    }
  }

  &__facet-label {
    flex: 1;
  }

  &__facet-count {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__grid {
    overflow: hidden;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    table {
      width: 100%;
      border-collapse: collapse;
    }

    thead th {
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: .05em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);
      padding: 10px 14px;
      border-bottom: 1px solid var(--theme-border);
      background: var(--theme-bg-content);
      white-space: nowrap;
    }

    tbody td {
      padding: 10px 14px;
      font-size: 12.5px;
      border-bottom: 1px solid var(--theme-border-soft);
      vertical-align: middle;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:hover {
      background: var(--theme-bg-content);
      cursor: pointer;
    }
  }

  &__loading {
    text-align: center;
    color: var(--theme-text-tertiary);
    padding: 24px !important;
  }

  &__num {
    font-variant-numeric: tabular-nums;
  }

  &__na {
    color: var(--theme-text-tertiary);
  }

  &__health-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;

    &.h-ok { background: var(--theme-status-success); }
    &.h-warn { background: var(--theme-status-warning); }
    &.h-crit { background: var(--theme-status-danger); }
    &.h-unk { background: #fff; border: 1.5px dashed var(--theme-text-tertiary); width: 6px; height: 6px; }
    &.h-pending { background: var(--theme-status-info); }
  }

  &__srv-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__srv-name {
    font-family: var(--theme-font-mono);
    font-weight: 600;
    font-size: 12.5px;
    display: block;
    white-space: nowrap;
  }

  &__srv-provider {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 1px;
  }

  &__pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;
    margin-right: 4px;

    &--env {
      background: var(--theme-border-soft);
      color: var(--theme-text-secondary);
    }

    &--role {
      background: var(--theme-accent-light);
      color: var(--theme-accent-dark);
    }
  }

  // Plan 011 §5.2 — cert-expiry column. Amber < 30 days, red < 7, grey
  // "unknown" (every server until the cert-reporting agent ships).
  &__cert-pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &--cert-ok {
      background: var(--theme-border-soft);
      color: var(--theme-text-secondary);
    }

    &--cert-warn {
      background: #fbeecd;
      color: #8a6116;
    }

    &--cert-crit {
      background: #f8dede;
      color: #a53232;
    }

    &--cert-unknown {
      background: var(--theme-border-soft);
      color: var(--theme-text-tertiary);
    }
  }

  &__meter-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__meter {
    width: 56px;
    height: 6px;
    border-radius: 3px;
    background: var(--theme-border-soft);
    overflow: hidden;
    flex-shrink: 0;
    display: inline-block;
  }

  &__meter-fill {
    height: 100%;
    border-radius: 3px;
    display: block;

    &.m-ok { background: var(--theme-status-success); }
    &.m-warn { background: var(--theme-status-warning); }
    &.m-crit { background: var(--theme-status-danger); }
  }

  &__meter-val {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    width: 30px;
    text-align: right;
    display: inline-block;
  }

  &__install-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: var(--theme-accent-primary);
    color: #fff;
    border: none;
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }

  &__row-view {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }
}
</style>
